import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { DepartmentDisplay } from '@modules/department/models/department-display';
import { PatientAvatarComponent } from '@modules/patient/patient-avatar/patient-avatar.component';
import { PatientDataComponent } from '@modules/patient/patient-data/patient-data.component';
import { LottieLabelComponent } from '@modules/shared/lottie-label/lottie-label.component';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { AvatarStyle } from '@pixacare/shared';
import { AvatarStyleService } from '@services/avatar-style.service';
import { Size } from '@shared/models/enums/size.enum';
import { departmentsActions } from '@shared/store/departments/departments.actions';
import {
  selectClientDepartments,
  selectIsDepartmentStateLoaded,
} from '@shared/store/departments/departments.selectors';
import { TuiButton, TuiDialogContext, TuiIcon } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { map, Observable } from 'rxjs';

@Component({
  templateUrl: './patient-department-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TuiIcon,
    TuiButton,
    TuiBlockStatus,
    TuiSkeleton,
    TranslocoDirective,
    LottieLabelComponent,
    PatientAvatarComponent,
    PatientDataComponent,
  ],
})
export class PatientDepartmentPickerComponent {
  private readonly avatarStyleService = inject(AvatarStyleService);
  private readonly store = inject(Store);
  private readonly context = inject<
    TuiDialogContext<
      number,
      {
        patient: Patient;
        clientCode: string;
      }
    >
  >(POLYMORPHEUS_CONTEXT);

  @Input() departments$: Observable<DepartmentDisplay[]>;
  @Input() patient: Patient;
  @Input() clientCode: string;

  isDepartmentsStateLoaded$: Observable<boolean>;

  Size = Size;

  constructor() {
    this.patient = this.context.data.patient;
    this.clientCode = this.context.data.clientCode;
    this.store.dispatch(departmentsActions.ensureClientDepartments({ clientCode: this.clientCode }));
    this.isDepartmentsStateLoaded$ = this.store.select(selectIsDepartmentStateLoaded(this.clientCode));
    this.departments$ = this.store
      .select(selectClientDepartments(this.clientCode))
      .pipe(
        map((departments) => departments.filter((department) => this.patient.departmentIds.includes(department.id))),
      );
  }

  getDepartmentAvatarStyle(department: DepartmentDisplay): AvatarStyle {
    return this.avatarStyleService.getAvatarStyle(department.name);
  }

  pickDepartment(departmentId: number): void {
    this.context.completeWith(departmentId);
  }

  cancel(): void {
    this.context.completeWith(null);
  }
}
